import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'
import { useCallback, useEffect, useRef, useState } from 'react'

import { EOptionalEnvironmentVariables } from '~/models/config'

const SITE_KEY = import.meta.env[
  EOptionalEnvironmentVariables.VITE_TURNSTILE_SITE_KEY
] as string | undefined

// Cloudflare Turnstile tokens are valid for 300s and strictly single-use —
// replaying one (or using one past its window) gets rejected by siteverify
// with `timeout-or-duplicate`. Confirmed root cause of a real incident (Sep
// 2026 Cloudflare analytics: 100% client-side solve rate but only ~4% of
// siteverify calls valid): a conversation tab left open but backgrounded
// gets its JS timers throttled by the browser, so Turnstile's own
// `refreshExpired: 'auto'` background re-solve can lag well past 300s.
// getToken() previously handed back whatever was cached with no age check,
// so a reconnect on a long-backgrounded tab (tab regains focus, or the
// socket wakes up after being suspended) sent a guaranteed-stale token
// straight into a doomed /token request. A margin under 300s (not exactly
// 300s) leaves room for the /token request + gateway-side siteverify
// round-trip itself to complete before the token would expire mid-flight.
const MAX_TOKEN_AGE_MS = 4 * 60 * 1000

// @marsidev/react-turnstile defaults to retry: 'auto' with an 8s
// retry-interval (Cloudflare's own built-in auto-retry on error — see the
// installed package, not something this hook opts into). onError previously
// called widgetRef.reset() unconditionally on every single error callback,
// racing Cloudflare's own in-flight auto-retry: two independent re-solve
// attempts landing on the same widget/container around the same moment,
// which is what produced "two calls to challenges.cloudflare.com in quick
// succession, second one 401s" plus the "Ignored message from unexpected
// source" / postMessage-origin-mismatch console noise (one attempt's widget
// context getting invalidated mid-flight by the other). Comfortably above
// 8000ms so Cloudflare's own retry gets a full cycle to recover on its own
// before this hook's fallback reset ever steps in.
const ERROR_RESET_COOLDOWN_MS = 10 * 1000

// Bounds getFreshToken's wait below — long enough to cover a normal
// invisible-mode solve (typically well under a second, but this also
// covers the reset-then-resolve round trip when the cached token was
// merely stale), short enough that a guest-facing REST retry waiting on
// this doesn't hang indefinitely if Turnstile itself is down.
const GET_FRESH_TOKEN_TIMEOUT_MS = 8 * 1000
const GET_FRESH_TOKEN_POLL_MS = 100

export type TTurnstileTokenAccessor = {
  /** Current solved token, or '' if none is ready yet. */
  getToken: () => string
  /**
   * Like getToken(), but waits briefly for a fresh solve instead of handing
   * back '' immediately when the cached token is stale or doesn't exist yet.
   * Every mint of a guest JWT (connect()'s initial one, and
   * refreshGuestToken's self-heal one) needs a real turnstileToken whenever
   * Turnstile is configured server-side — a long-lived session's guest JWT
   * (5-minute lifetime) and its Turnstile solve (MAX_TOKEN_AGE_MS, 4
   * minutes) tend to go stale around the same time, so a refresh triggered
   * by the JWT expiring could otherwise hit getToken() returning '' and send
   * a /token POST missing turnstileToken, which a Turnstile-required
   * gateway rejects outright — breaking self-heal in exactly the scenario
   * it exists for (Cursor-flagged, High severity). Use this for any /token
   * mint; use the synchronous getToken() only where blocking briefly isn't
   * acceptable.
   */
  getFreshToken: () => Promise<string>
  /**
   * Call once a token has actually been sent in a /token request — Cloudflare
   * Turnstile tokens are single-use server-side, so this clears the cached
   * value and forces a fresh solve for the NEXT connect/reconnect attempt
   * rather than risking a resend of an already-consumed token.
   */
  consumeToken: () => void
  /**
   * True once a token is available to send. When VITE_TURNSTILE_SITE_KEY is
   * unset this is `true` from the start (nothing to wait for) — mirrors this
   * codebase's "missing config = feature off" convention (see WS_URL in
   * useChatV2) rather than blocking the widget entirely on a var nobody set.
   */
  isReady: boolean
  /** Render this once, anywhere in the tree — invisible widget, no layout. */
  widget: JSX.Element | null
}

// heyLibbyChat's gateway only ENFORCES Turnstile when TURNSTILE_SECRET_KEY is
// configured server-side (services/gateway-service/src/handlers/tokenRequest.ts) —
// this hook mirrors that on the frontend: it's a complete no-op (no script
// injected, no widget rendered, getToken() always '') when the site key isn't
// set, so local dev and any environment that hasn't set up Turnstile yet is
// completely unaffected.
function useTurnstileToken(): TTurnstileTokenAccessor {
  const tokenRef = useRef('')
  // When the current tokenRef value was actually solved — separate from any
  // React state so getToken() can check it synchronously without triggering
  // a re-render. 0 means "no token" (matches tokenRef.current === '').
  const tokenObtainedAtRef = useRef(0)
  const widgetRef = useRef<TurnstileInstance>(null)
  const lastErrorResetAtRef = useRef(0)
  const [isReady, setIsReady] = useState(!SITE_KEY)

  const clearToken = useCallback(() => {
    tokenRef.current = ''
    tokenObtainedAtRef.current = 0
  }, [])

  const getToken = useCallback(() => {
    if (!tokenRef.current) return ''
    if (Date.now() - tokenObtainedAtRef.current > MAX_TOKEN_AGE_MS) {
      // Stale — almost certainly already past Cloudflare's own 300s window
      // (see MAX_TOKEN_AGE_MS above). Handing this to a caller would just
      // burn a guaranteed-invalid siteverify call; clear it and kick off a
      // fresh solve now instead, so the caller's own retry has a real token
      // to use shortly rather than repeating the same stale one.
      clearToken()
      widgetRef.current?.reset()
      return ''
    }
    return tokenRef.current
  }, [clearToken])

  const consumeToken = useCallback(() => {
    clearToken()
    widgetRef.current?.reset()
  }, [clearToken])

  const getFreshToken = useCallback(async (): Promise<string> => {
    // Without a SITE_KEY no widget is ever rendered (see the no-op return
    // below), so tokenRef.current can never become truthy — getToken()
    // always returns '' here, and without this check the poll loop below
    // would wait out the full GET_FRESH_TOKEN_TIMEOUT_MS on every single
    // call for nothing, adding a flat 8s to every WebSocket connect and
    // every self-heal retry in any environment that hasn't configured
    // Turnstile (local dev included) — Cursor-flagged, High severity.
    if (!SITE_KEY) return ''
    const immediate = getToken()
    if (immediate) return immediate
    // getToken() above already triggered a reset (the cached token was
    // merely stale) or the widget is still completing its very first solve
    // (none existed yet) — either way, wait briefly for the NEXT onSuccess
    // to land instead of handing back '' right away.
    const startedAt = Date.now()
    while (Date.now() - startedAt < GET_FRESH_TOKEN_TIMEOUT_MS) {
      if (tokenRef.current) return tokenRef.current
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, GET_FRESH_TOKEN_POLL_MS)
      })
    }
    return ''
  }, [getToken])

  // A tab backgrounded long enough for Turnstile's own auto-refresh timer to
  // get throttled past MAX_TOKEN_AGE_MS won't naturally get a fresh token
  // until something actually calls getToken() again (the next connect
  // attempt). Proactively re-solving the moment the tab regains focus means
  // that next attempt already has a fresh token ready, instead of first
  // wasting a round trip discovering the cached one was stale.
  useEffect(() => {
    if (!SITE_KEY) return () => {}

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!tokenRef.current) return
      if (Date.now() - tokenObtainedAtRef.current <= MAX_TOKEN_AGE_MS) return
      clearToken()
      widgetRef.current?.reset()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [clearToken])

  if (!SITE_KEY) {
    return { getToken, getFreshToken, consumeToken, isReady, widget: null }
  }

  const widget = (
    <Turnstile
      ref={widgetRef}
      siteKey={SITE_KEY}
      options={{ size: 'invisible' }}
      onSuccess={(token) => {
        tokenRef.current = token
        tokenObtainedAtRef.current = Date.now()
        setIsReady(true)
      }}
      onExpire={() => {
        // Default refreshExpired ('auto') already re-solves in the background;
        // explicit reset here just makes that immediate rather than waiting
        // for Turnstile's own retry timing.
        clearToken()
        widgetRef.current?.reset()
      }}
      onError={() => {
        // Previously left isReady stuck at whatever it was and never
        // retried — a single transient Turnstile error (network blip,
        // Cloudflare-side hiccup) before the widget ever solved once meant
        // connect() (see useChatV2) waited forever with no recovery path
        // and no guest-visible signal at all, just this console.error
        // (confirmed live: staging guests hitting a Turnstile 401 got
        // permanently stuck on "Cannot send — session not ready yet").
        // reset() mirrors onExpire's existing behavior and lets Turnstile's
        // own widget attempt a fresh solve instead of staying dead for the
        // rest of the page's lifetime.
        //
        // Cooldown-gated: Turnstile's own default config (retry: 'auto',
        // retry-interval 8s — see ERROR_RESET_COOLDOWN_MS above) already
        // auto-retries on error without any help from us. Resetting
        // unconditionally on every onError raced that built-in retry —
        // two independent re-solve attempts on the same widget around the
        // same moment, which is what produced a second, failing
        // challenges.cloudflare.com call right after the first. Only step
        // in with our own reset once Cloudflare's own retry cycle has had
        // room to recover on its own and still hasn't.
        clearToken()
        const now = Date.now()
        if (now - lastErrorResetAtRef.current < ERROR_RESET_COOLDOWN_MS) {
          return
        }
        lastErrorResetAtRef.current = now
        widgetRef.current?.reset()
        // eslint-disable-next-line no-console
        console.error('[Turnstile] Widget error — retrying.')
      }}
    />
  )

  return { getToken, getFreshToken, consumeToken, isReady, widget }
}

export default useTurnstileToken
